import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import type { DocumentUploadEntity } from '@izimi/core';
import { isCertinotError, useRemoveDocumentUploadEntity, useUpdateUploadEntity } from '@izimi/core';
import { Tracker, useModules } from '@izimi/modules';
import { formatBytes } from '@izimi/utils';

import { useTracking } from '../../tracking';
import { DocumentTableRowUploadView } from './DocumentTableRowUploadView';

interface Props {
  documentUploadEntity: DocumentUploadEntity;
  maxSizeFormatted?: string;
}

export const DocumentTableRowUpload = ({ documentUploadEntity, maxSizeFormatted }: Props) => {
  const { t } = useTranslation('shared');
  const {
    // @ts-expect-error extended
    documents: { useUploadDocument },
    public: { useWebConfiguration },
  } = useModules();
  const { trackEvent } = useTracking();
  const { configuration } = useWebConfiguration();
  const {
    uploadDocument,
    progress,
    isPending: isPendingUploadDocument,
    cancelUpload,
  } = useUploadDocument();
  const removeDocumentUploadEntity = useRemoveDocumentUploadEntity();
  const updateUploadEntity = useUpdateUploadEntity();

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    return () => cancelUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentUpload = useCallback(async () => {
    try {
      setErrorMessage('');
      updateUploadEntity(documentUploadEntity.fileId, { uploadStarted: true });
      await uploadDocument({
        file: documentUploadEntity.file,
        categories: documentUploadEntity.categories,
        labels: documentUploadEntity.labels,
        isShareable: documentUploadEntity.isShareable,
      });
      updateUploadEntity(documentUploadEntity.fileId, { uploadSuccess: true });
    } catch (error) {
      updateUploadEntity(documentUploadEntity.fileId, {
        uploadAborted: true,
      });
      if (error.message === 'client.technical.uploadAborted') {
        setErrorMessage(t('vault.upload.aborted'));
        return;
      }

      // errors thrown from different source than API will not have a userMessage
      if (!error.userMessage) {
        Sentry.withScope((scope) => {
          scope.setTags({
            description: 'Upload document failed due to internal error',
          });
          Sentry.captureException(error);
        });
        setErrorMessage(t('vault.upload.notUploadedBecauseInternalError'));
        return;
      }

      if (isCertinotError(error)) {
        switch (error.userMessage) {
          case 'client.technical.vaultlimitexceeded':
            setErrorMessage(
              t('vault.upload.vaultLimitExceeded', {
                size: maxSizeFormatted,
              }),
            );
            break;
          case 'client.technical.unsupportedfiletype':
            setErrorMessage(
              t('vault.upload.unsupportedFiletype', {
                type: `.${documentUploadEntity.extension}`,
              }),
            );
            break;
          case 'client.technical.unsupportedmedia':
            setErrorMessage(
              t('vault.upload.unsupportedMedia', {
                type: `.${documentUploadEntity.extension}`,
              }),
            );
            break;
          case 'client.technical.maxfilesizeexceeded':
            setErrorMessage(
              t('vault.upload.maxfileSizeExceeded', {
                size: formatBytes(configuration?.vaultConfig.maxAllowedFileSizeInBytes as number),
              }),
            );
            break;
          case 'client.technical.validation':
            setErrorMessage(t('vault.upload.documentNameInvalid'));
            break;
          default:
            setErrorMessage(t('vault.upload.notUploadedBecauseInternalError'));
        }
      } else {
        setErrorMessage(t('vault.upload.notUploadedBecauseInternalError'));
      }
    }
  }, [
    documentUploadEntity,
    updateUploadEntity,
    configuration,
    t,
    maxSizeFormatted,
    uploadDocument,
  ]);

  const handleClientErrors = useCallback(() => {
    if (!documentUploadEntity.fileTypeAllowed) {
      return setErrorMessage(
        t('vault.upload.unsupportedFiletype', {
          type: `.${documentUploadEntity.extension}`,
        }),
      );
    }
    if (!documentUploadEntity.fileSizeAllowed) {
      return setErrorMessage(
        t('vault.upload.maxfileSizeExceeded', {
          size: formatBytes(configuration?.vaultConfig.maxAllowedFileSizeInBytes as number),
        }),
      );
    }
    if (!documentUploadEntity.fileNameAllowed) {
      return setErrorMessage(t('vault.upload.documentNameInvalid'));
    }
    return setErrorMessage(t('vault.upload.notUploadedBecauseInternalError'));
  }, [
    configuration?.vaultConfig.maxAllowedFileSizeInBytes,
    documentUploadEntity.fileTypeAllowed,
    documentUploadEntity.fileNameAllowed,
    documentUploadEntity.extension,
    documentUploadEntity.fileSizeAllowed,
    t,
  ]);

  useEffect(() => {
    if (
      documentUploadEntity.uploadSuccess &&
      documentUploadEntity.uploadStarted &&
      documentUploadEntity.isValid
    ) {
      trackEvent({
        category: Tracker.Category.Uploading,
        action: Tracker.Action.UploadedDocument,
        value: 1,
      });
    }

    if (
      documentUploadEntity.isValid &&
      !documentUploadEntity.uploadSuccess &&
      !documentUploadEntity.uploadStarted
    ) {
      handleDocumentUpload();
    }
    if (!documentUploadEntity.isValid && !documentUploadEntity.uploadSuccess) {
      handleClientErrors();
    }
    // trackEvent is not pure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleDocumentUpload,
    documentUploadEntity.isValid,
    documentUploadEntity.uploadSuccess,
    handleClientErrors,
  ]);

  return (
    <DocumentTableRowUploadView
      documentUploadEntity={documentUploadEntity}
      errorMessage={errorMessage}
      progress={progress}
      isPendingUploadDocument={isPendingUploadDocument}
      abortLabel={t('action.abort')}
      removeLabel={t('vault.upload.remove')}
      onAbort={cancelUpload}
      onRemove={() => removeDocumentUploadEntity(documentUploadEntity.fileId)}
    />
  );
};
